import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Utensils, User, Mail, Lock, Building2, Bike, ChefHat, Phone } from "lucide-react";

const ROLES = [
  { key: "customer", label: "Customer", icon: User, desc: "Order food from island's best" },
  { key: "driver", label: "Driver", icon: Bike, desc: "Earn while you ride" },
  { key: "restaurant", label: "Restaurant", icon: ChefHat, desc: "Serve more customers" },
];

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("customer");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    // Drivers must give a real number in E.164 format so approval SMS lands.
    if (role === "driver") {
      const clean = phone.trim();
      if (!clean) {
        toast.error("Please enter your phone number");
        return;
      }
      if (!clean.startsWith("+") || !clean.slice(1).replace(/\s/g, "").match(/^\d+$/)) {
        toast.error("Phone must be in international format, e.g. +14735551234");
        return;
      }
    }
    setLoading(true);
    try {
      const u = await register({ email, password, name, role, phone: phone.trim() });
      toast.success("Account created — good food incoming!");
      if (u.role === "driver") navigate("/driver/onboarding");
      else if (u.role === "restaurant") navigate("/restaurant-admin");
      else navigate("/home");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-eatup-gray flex items-center justify-center p-6">
      <div className="w-full max-w-lg bg-white rounded-3xl card-shadow p-8 sm:p-10">
        <div className="flex items-center gap-2 mb-8">
          <img src="/eats-gnd-logo.png" alt="Eats GND" className="w-11 h-11 rounded-2xl object-cover shadow-md" draggable={false} />
          <span className="font-display text-2xl font-bold">Eats <span className="text-eatup-green">GND</span></span>
        </div>

        <h1 className="font-display text-3xl sm:text-4xl font-bold">Create your account</h1>
        <p className="mt-1 text-neutral-500">Pick a role and let's get you started.</p>

        <div className="mt-6 grid grid-cols-3 gap-2">
          {ROLES.map((r) => {
            const Icon = r.icon;
            const active = role === r.key;
            return (
              <button
                key={r.key}
                data-testid={`role-${r.key}`}
                onClick={() => setRole(r.key)}
                className={`p-4 rounded-2xl border transition text-left ${
                  active
                    ? "border-eatup-green bg-eatup-green/5 ring-2 ring-eatup-green/20"
                    : "border-neutral-200 hover:border-neutral-300"
                }`}
              >
                <Icon className={`w-5 h-5 mb-2 ${active ? "text-eatup-green" : "text-neutral-500"}`} />
                <div className="text-sm font-semibold">{r.label}</div>
                <div className="text-xs text-neutral-500 mt-0.5 line-clamp-2">{r.desc}</div>
              </button>
            );
          })}
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="relative">
            <User className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              data-testid="register-name-input"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              className="w-full pl-11 pr-4 py-3.5 rounded-2xl bg-eatup-gray border border-transparent focus:border-eatup-green focus:bg-white focus:ring-4 focus:ring-eatup-green/10 outline-none transition"
            />
          </div>
          <div className="relative">
            <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              data-testid="register-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="w-full pl-11 pr-4 py-3.5 rounded-2xl bg-eatup-gray border border-transparent focus:border-eatup-green focus:bg-white focus:ring-4 focus:ring-eatup-green/10 outline-none transition"
            />
          </div>
          <div className="relative">
            <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              data-testid="register-password-input"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password (min 6 chars)"
              className="w-full pl-11 pr-4 py-3.5 rounded-2xl bg-eatup-gray border border-transparent focus:border-eatup-green focus:bg-white focus:ring-4 focus:ring-eatup-green/10 outline-none transition"
            />
          </div>
          {role === "driver" && (
            <div className="relative" data-testid="register-phone-wrapper">
              <Phone className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                data-testid="register-phone-input"
                type="tel"
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone (e.g. +14735551234)"
                className="w-full pl-11 pr-4 py-3.5 rounded-2xl bg-eatup-gray border border-transparent focus:border-eatup-green focus:bg-white focus:ring-4 focus:ring-eatup-green/10 outline-none transition"
              />
              <p className="mt-1.5 ml-1 text-[11px] text-neutral-500" data-testid="register-phone-hint">
                We'll SMS you the moment your application is approved.
              </p>
            </div>
          )}
          <button data-testid="register-submit-btn" disabled={loading} className="w-full btn-primary disabled:opacity-60">
            {loading ? "Creating account…" : "Create account"}
          </button>
        </form>

        <p className="mt-6 text-sm text-neutral-500 text-center">
          Already have an account?{" "}
          <Link to="/login" className="text-eatup-green font-semibold hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
